import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { parseLocalDate } from '../../../utils/date-time-formatter';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';


export class ForkliftInspection {
    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date;

    // Basic Information
    date: Date | null = null;
    jobNameNumber: string = '';
    
    // Engine & Power Systems Section
    battery: string = '';
    batteryComments: string = '';
    engineOil: string = '';
    engineOilComments: string = '';
    coolant: string = '';
    coolantComments: string = '';
    hydraulicFluid: string = '';
    hydraulicFluidComments: string = '';
    hydraulicLines: string = '';
    hydraulicLinesComments: string = '';
    transmissionFluid: string = '';
    transmissionFluidComments: string = '';
    propaneTank: string = '';
    propaneTankComments: string = '';
    propaneHose: string = '';
    propaneHoseComments: string = '';
    horn: string = '';
    hornComments: string = '';
    lights: string = '';
    lightsComments: string = '';
    reverseBeeper: string = '';
    reverseBeeperComments: string = '';

    // Mechanical & Safety Systems Section
    forks: string = '';
    forksComments: string = '';
    carriage: string = '';
    carriageComments: string = '';
    liftChainsRollers: string = '';
    liftChainsRollersComments: string = '';
    raiseLower: string = '';
    raiseLowerComments: string = '';
    tilt: string = '';
    tiltComments: string = '';
    sideShift: string = '';
    sideShiftComments: string = '';
    steering: string = '';
    steeringComments: string = '';
    brakes: string = '';
    brakesComments: string = '';
    tires: string = '';
    tiresComments: string = '';
    mainBodyOfLiftTruck: string = '';
    mainBodyOfLiftTruckComments: string = '';
    overheadGuard: string = '';
    overheadGuardComments: string = '';
    boltsPins: string = '';
    boltsPinsComments: string = '';
    boomIndicator: string = '';
    boomIndicatorComments: string = '';
    glassMirrors: string = '';
    glassMirrorsComments: string = '';
    seatBelts: string = '';
    seatBeltsComments: string = '';
    fireExtinguisher: string = '';
    fireExtinguisherComments: string = '';
    warningDecals: string = '';
    warningDecalsComments: string = '';
    riggingShackles: string = '';
    riggingShacklesComments: string = '';
    lubricantFittings: string = '';
    lubricantFittingsComments: string = '';

    // Sign-off Section
    operatorName: string = '';
    operatorSignature: Array<string> = [];
    generalComments: string = '';

    constructor(data?: Partial<ForkliftInspection>) {
        if (data) {
            Object.assign(this, {
                ...data,
                date: data.date ? parseLocalDate(data.date as any) : null,
                submissionDate: data.submissionDate ? new Date(data.submissionDate) : null
            });
        }
    }


    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'engine-power',
                shortTitle: 'Engine & Power',
                title: 'Engine & Power Systems',
                icon: 'speedometer-outline',
                description: 'Inspect engine, power, and fluid systems',
                sections: [
                    {
                        title: 'Basic Information',
                        description: 'Date and job information',
                        fields: ['date', 'jobNameNumber']
                    },
                    {
                        title: 'Are all items accounted for and working properly?',
                        fields: ['battery', 'batteryComments', 'engineOil', 'engineOilComments', 'coolant', 'coolantComments', 'hydraulicFluid', 'hydraulicFluidComments', 'hydraulicLines', 'hydraulicLinesComments', 'transmissionFluid', 'transmissionFluidComments', 'propaneTank', 'propaneTankComments', 'propaneHose', 'propaneHoseComments', 'horn', 'hornComments', 'lights', 'lightsComments', 'reverseBeeper', 'reverseBeeperComments']
                    }
                ]
            },
            {
                id: 'mechanical-safety',
                shortTitle: 'Mechanical & Safety',
                title: 'Mechanical & Safety Systems',
                icon: 'cog-outline',
                description: 'Inspect mechanical operation and safety equipment',
                sections: [
                    {
                        title: 'Are all items accounted for and working properly?',
                        fields: ['forks', 'forksComments', 'carriage', 'carriageComments', 'liftChainsRollers', 'liftChainsRollersComments', 'raiseLower', 'raiseLowerComments', 'tilt', 'tiltComments', 'sideShift', 'sideShiftComments', 'steering', 'steeringComments', 'brakes', 'brakesComments', 'tires', 'tiresComments', 'mainBodyOfLiftTruck', 'mainBodyOfLiftTruckComments', 'overheadGuard', 'overheadGuardComments', 'boltsPins', 'boltsPinsComments', 'boomIndicator', 'boomIndicatorComments', 'glassMirrors', 'glassMirrorsComments', 'seatBelts', 'seatBeltsComments', 'fireExtinguisher', 'fireExtinguisherComments', 'warningDecals', 'warningDecalsComments', 'riggingShackles', 'riggingShacklesComments', 'lubricantFittings', 'lubricantFittingsComments']
                    }
                ]
            },
            {
                id: 'signOff',
                shortTitle: 'Sign-off',
                title: 'Inspection Sign-off',
                icon: 'pencil-outline',
                description: 'Complete the inspection with operator information',
                sections: [
                    {
                        fields: ['operatorName', 'operatorSignature','generalComments', ]
                    }
                ]
            }
        ];
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'forklift-inspection',
            formTitle: 'Daily Forklift Inspection',
            formIcon: 'swap-vertical-outline',
            cardImage: 'assets/form-card-tiles/forklift-inspection.png',
            formAttachmentRequirements: {
                signatures: [
                    {
                        identifier: 'operatorSignature',
                        label: this.getFieldMappings()['operatorSignature'],
                        required: true,
                      }
                    ]
            }
        };
    }

    static getFieldMappings(): Record<string, string> {
        return {
            submittedByName: "Submitter Name",
            submittedByEmail: "Submitter Email",
            submissionDate: "Submission Date",
            date: "Date",
            jobNameNumber: "Job Name/Number",
            
            // Engine & Power Systems Section
            battery: "Battery",
            batteryComments: "Battery Comments",
            engineOil: "Engine Oil",
            engineOilComments: "Engine Oil Comments",
            coolant: "Coolant",
            coolantComments: "Coolant Comments",
            hydraulicFluid: "Hydraulic Fluid",
            hydraulicFluidComments: "Hydraulic Fluid Comments",
            hydraulicLines: "Hydraulic Lines",
            hydraulicLinesComments: "Hydraulic Lines Comments",
            transmissionFluid: "Transmission Fluid",
            transmissionFluidComments: "Transmission Fluid Comments",
            propaneTank: "Propane Tank",
            propaneTankComments: "Propane Tank Comments",
            propaneHose: "Propane Hose",
            propaneHoseComments: "Propane Hose Comments",
            horn: "Horn",
            hornComments: "Horn Comments",
            lights: "Lights",
            lightsComments: "Lights Comments",
            reverseBeeper: "Reverse Beeper",
            reverseBeeperComments: "Reverse Beeper Comments",

            // Mechanical & Safety Systems Section
            forks: "Forks",
            forksComments: "Forks Comments",
            carriage: "Carriage",
            carriageComments: "Carriage Comments",
            liftChainsRollers: "Lift Chains/Rollers",
            liftChainsRollersComments: "Lift Chains/Rollers Comments",
            raiseLower: "Raise/Lower",
            raiseLowerComments: "Raise/Lower Comments",
            tilt: "Tilt",
            tiltComments: "Tilt Comments",
            sideShift: "Side Shift",
            sideShiftComments: "Side Shift Comments",
            steering: "Steering",
            steeringComments: "Steering Comments",
            brakes: "Brakes",
            brakesComments: "Brakes Comments",
            tires: "Tires",
            tiresComments: "Tires Comments",
            mainBodyOfLiftTruck: "Main Body of Lift Truck",
            mainBodyOfLiftTruckComments: "Main Body of Lift Truck Comments",
            overheadGuard: "Overhead Guard",
            overheadGuardComments: "Overhead Guard Comments",
            boltsPins: "Bolts/Pins",
            boltsPinsComments: "Bolts/Pins Comments",
            boomIndicator: "Boom Indicator",
            boomIndicatorComments: "Boom Indicator Comments",
            glassMirrors: "Glass/Mirrors",
            glassMirrorsComments: "Glass/Mirrors Comments",
            seatBelts: "Seat Belts",
            seatBeltsComments: "Seat Belts Comments",
            fireExtinguisher: "Fire Extinguisher",
            fireExtinguisherComments: "Fire Extinguisher Comments",
            warningDecals: "Warning Decals",
            warningDecalsComments: "Warning Decals Comments",
            riggingShackles: "Rigging/Shackles",
            riggingShacklesComments: "Rigging/Shackles Comments",
            lubricantFittings: "Lubricant/Fittings",
            lubricantFittingsComments: "Lubricant/Fittings Comments",

            // Sign-off Section
            operatorName: "Operator Name",
            operatorSignature: "Operator Signature",
            generalComments: "Comments"
        };
    }
} 